import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { fetchPlayerOverview, fetchPlayerMatchesMeta } from './api';
import { useMappedPlayerId } from './hooks';
import PlayerMatchesList from '../../components/PlayerMatchesList';
import PlayerMatchesFilterBar from '../../components/PlayerMatchesFilterBar';
import PlayerLoader from '../../components/PlayerLoader';
import { useLexicon } from '../../hooks/useLexicon';

const PlayerMatches = () => {
  const l = useLexicon();
  const mappedPlayerId = useMappedPlayerId();

  const [overview, setOverview] = useState(null);
  const [matchesMeta, setMatchesMeta] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPlayerOverview(mappedPlayerId, selectedSeasonId || undefined)
      .then((res) => {
        if (cancelled) return;
        setOverview(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mappedPlayerId, selectedSeasonId]);

  // Fetch player.teams once — drives the W/L/D coloring on PlayerMatchesList.
  // Loaded independently of the season-scoped overview so it doesn't refetch
  // when the user changes seasons.
  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    fetchPlayerMatchesMeta(mappedPlayerId)
      .then((res) => { if (!cancelled) setMatchesMeta(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mappedPlayerId]);

  const seasons = useMemo(
    () => (Array.isArray(overview?.available_seasons) ? overview.available_seasons : []),
    [overview?.available_seasons]
  );

  useEffect(() => {
    if (selectedSeasonId || seasons.length === 0) return;
    setSelectedSeasonId(String(seasons[0].id));
  }, [seasons, selectedSeasonId]);

  const matches = useMemo(
    () => (Array.isArray(overview?.matches_list) ? overview.matches_list : []),
    [overview?.matches_list]
  );

  const competitions = useMemo(() => {
    const map = new Map();
    matches.forEach((m) => {
      const id = m.competition_id ?? m.competition;
      const name = typeof m.competition === 'string'
        ? m.competition
        : m.competition?.name;
      if (id != null && name && !map.has(id)) {
        map.set(id, { id, name });
      }
    });
    return Array.from(map.values());
  }, [matches]);

  const filteredMatches = useMemo(() => {
    if (!selectedCompetitionId) return matches;
    return matches.filter((m) => {
      const id = m.competition_id ?? m.competition;
      return String(id) === String(selectedCompetitionId);
    });
  }, [matches, selectedCompetitionId]);

  const playerTeams = matchesMeta?.player?.teams
    ?? overview?.player?.teams
    ?? overview?.teams
    ?? [];

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  return (
    <Wrap>
      <Title>{l(1821)}</Title>

      <PlayerMatchesFilterBar
        seasons={seasons}
        selectedSeasonId={selectedSeasonId}
        onSeasonChange={setSelectedSeasonId}
        competitions={competitions}
        selectedCompetitionId={selectedCompetitionId}
        onCompetitionChange={setSelectedCompetitionId}
      />

      {error && <ErrorBox>{l(1822)}</ErrorBox>}
      {!loading && !error && filteredMatches.length === 0 && (
        <Muted>{l(1823)}</Muted>
      )}

      {filteredMatches.length > 0 && (
        <PlayerMatchesList matches={filteredMatches} playerTeams={playerTeams} />
      )}

      {loading && <PlayerLoader label={l(1824)} />}
    </Wrap>
  );
};

export default PlayerMatches;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;
